import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, User, FileText, Plus, Loader, MessageSquare } from 'lucide-react';
import ragService from '../../api/services/ragService';

export default function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await ragService.listConversations();
      if (res.success) setConversations(res.data || []);
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const openConversation = async (id) => {
    setConversationId(id);
    setError(null);
    try {
      const res = await ragService.getMessages(id);
      if (res.success) {
        setMessages(res.data.map((m) => ({
          role: m.role,
          content: m.content,
          citations: m.citations || [],
        })));
      }
    } catch (err) {
      setError(err.error?.message || 'Erreur de chargement');
    }
  };

  const newConversation = () => {
    setConversationId(null);
    setMessages([]);
    setError(null);
  };

  const handleSend = () => {
    const message = input.trim();
    if (!message || streaming) return;

    setInput('');
    setError(null);
    setMessages((m) => [...m, { role: 'user', content: message }, { role: 'assistant', content: '', citations: [] }]);
    setStreaming(true);

    abortRef.current = ragService.sendMessageStream(
      { message, conversationId },
      {
        onConversation: (id) => { if (!conversationId) setConversationId(id); },
        onDelta: (text) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + text };
            return copy;
          });
        },
        onCitations: (citations) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], citations };
            return copy;
          });
        },
        onError: (msg) => {
          setError(msg);
          setStreaming(false);
        },
        onDone: () => {
          setStreaming(false);
          loadConversations();
        },
      }
    );
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-120px)]">
      {/* Liste conversations */}
      <div className="hidden md:flex w-64 flex-col card p-3 flex-shrink-0">
        <button
          onClick={newConversation}
          className="w-full bg-accent-green text-black px-3 py-2 rounded-lg font-medium flex items-center justify-center gap-2 mb-3"
        >
          <Plus size={18} /> Nouvelle discussion
        </button>
        <div className="flex-1 overflow-y-auto space-y-1">
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => openConversation(c.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate transition-all flex items-center gap-2 ${
                conversationId === c.id ? 'bg-accent-green/20 text-accent-green' : 'text-label hover:bg-[var(--c-hover)]'
              }`}
            >
              <MessageSquare size={14} className="flex-shrink-0" />
              {c.title || 'Sans titre'}
            </button>
          ))}
        </div>
      </div>

      {/* Zone de chat */}
      <div className="flex-1 flex flex-col card overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Bot className="text-accent-green mb-3" size={48} />
              <p className="text-ink font-semibold text-lg">Pose une question à tes documents</p>
              <p className="text-label text-sm mt-1 max-w-md">
                L'IA répond uniquement à partir des documents que tu as indexés, avec citations des sources.
              </p>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  m.role === 'user' ? '' : 'bg-accent-green/20'
                }`}
                style={m.role === 'user' ? { background: 'var(--c-primary-dark)' } : {}}>
                  {m.role === 'user' ? <User size={16} className="text-label" /> : <Bot size={16} className="text-accent-green" />}
                </div>
                <div className={`max-w-[80%] ${m.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block px-4 py-2.5 rounded-2xl whitespace-pre-wrap text-left ${
                      m.role === 'user' ? 'bg-accent-green text-black' : 'text-ink'
                    }`}
                    style={m.role !== 'user' ? { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' } : {}}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <Loader size={16} className="animate-spin" /> : '')}
                  </div>
                  {m.citations?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.citations.map((c, idx) => (
                        <span
                          key={idx}
                          title={c.snippet}
                          className="text-xs px-2 py-1 text-label rounded flex items-center gap-1 cursor-help" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                        >
                          <FileText size={11} className="text-accent-green" />
                          {c.fileName || 'Source'}{c.page ? ` p.${c.page}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
          )}
          <div ref={endRef} />
        </div>

        {/* Saisie */}
        <div className="p-3" style={{ borderTop: '0.5px solid var(--c-border)' }}>
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              rows={1}
              placeholder="Pose ta question…"
              className="flex-1 resize-none text-ink px-4 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-accent-green/40"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
            <button
              onClick={handleSend}
              disabled={streaming || !input.trim()}
              className="bg-accent-green text-black px-4 rounded-lg font-medium disabled:opacity-50 flex items-center"
            >
              {streaming ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
